import React, { useEffect, useState } from 'react';
import { Card, Grid, Header, Statistic, Loader } from 'semantic-ui-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { API, showError } from '../../helpers';
import { renderNumber, renderWan } from '../../helpers/render';
import './Dashboard.css';

// 原生总览页：数据来自 Doris（/api/dashboard/*），由 DASHBOARD_DSN 配置。
// 替代原帆软决策大屏 iframe。

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#38dd90', '#d884a8', '#83a6ed', '#8dd1e1'];

const fmtDate = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const getLastSevenDays = () => {
  const now = new Date();
  return [fmtDate(new Date(now.getTime() - 6 * 86400000)), fmtDate(now)];
};

const renderAmount = (num) => {
  const n = Number(num || 0);
  const abs = Math.abs(n);
  if (abs >= 1e8) {
    return (n / 1e8).toFixed(2) + '亿';
  }
  if (abs >= 1e4) {
    return (n / 1e4).toFixed(2) + '万';
  }
  return n.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);
  const [stacked, setStacked] = useState([]);

  const load = async () => {
    const [t1, t2] = getLastSevenDays();
    try {
      const [trendRes, stackedRes] = await Promise.all([
        API.get(`/api/dashboard/trend?t1=${t1}&t2=${t2}`),
        API.get(`/api/dashboard/stacked?t1=${t1}&t2=${t2}`)
      ]);
      if (trendRes.data.success) setTrend(trendRes.data.data || []);
      else showError(trendRes.data.message);
      if (stackedRes.data.success) setStacked(stackedRes.data.data || []);
      else showError(stackedRes.data.message);
    } catch (e) {
      showError(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className='dashboard-container'>
        <Loader active>加载中...</Loader>
      </div>
    );
  }

  const today = trend.length ? trend[trend.length - 1] : null;
  // 每张卡片的单位不同，格式化函数必须跟着卡片走：
  // 三张折线图共用一个 formatter 会让「今日消费」卡片的悬浮值与卡片本体自相矛盾
  // （本体显示 ¥1.23万、悬浮显示 12.3k，既丢了 ¥ 也丢了万，缩写刻度还不同）。
  const cards = [
    { title: '今日请求量', format: renderNumber, field: 'request_count', color: '#8884d8' },
    { title: '今日消费 (¥)', format: (v) => '¥' + renderAmount(v), field: 'quota_cost', color: '#82ca9d' },
    // 后端 model/dashboard.go 已把 token 总量 /10000，此处单位是「万」，
    // 不能再走 renderNumber 的 B/M/k 二次缩写（25000 万会显示成 25.0k）
    { title: '今日 Token (万)', format: renderWan, field: 'token_cost', color: '#ffc658' },
  ].map((c) => ({ ...c, data: trend, value: today ? c.format(today[c.field]) : '-' }));
  // 数字过长时自动缩小字号，避免溢出卡片
  const statFontSize = (v) => {
    const len = String(v).length;
    if (len > 12) return '1.05em';
    if (len > 10) return '1.25em';
    if (len > 8) return '1.45em';
    return '1.6em';
  };

  // stacked: 按天+模型的 token 消费(输入+输出加和, 单位万)，前端透传成 recharts 需要的格式
  const models = [...new Set(stacked.map((d) => d.model_name))];
  const stackedByDay = {};
  stacked.forEach((d) => {
    if (!stackedByDay[d.day_short]) stackedByDay[d.day_short] = { date: d.day_short };
    stackedByDay[d.day_short][d.model_name] = (stackedByDay[d.day_short][d.model_name] || 0) + d.token_count;
  });
  const stackedData = Object.values(stackedByDay);

  return (
    <div className='dashboard-container'>
      <Grid stackable columns={3} className='stat-grid'>
        {cards.map((c) => (
          <Grid.Column key={c.title}>
            <Card fluid className='chart-card'>
              <Card.Content>
                <Statistic>
                  <Statistic.Label>{c.title}</Statistic.Label>
                  <Statistic.Value
                    className='stat-value'
                    style={{ fontSize: statFontSize(c.value) }}
                  >
                    {c.value}
                  </Statistic.Value>
                </Statistic>
                <ResponsiveContainer width='100%' height={56}>
                  <LineChart data={c.data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                    <XAxis dataKey='day_short' hide />
                    <YAxis hide domain={['dataMin', 'dataMax']} />
                    <Tooltip contentStyle={{ fontSize: 12, padding: '4px 8px' }} formatter={(v) => c.format(v)} />
                    <Line type='monotone' dataKey={c.field} stroke={c.color} dot={false} strokeWidth={2} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Content>
            </Card>
          </Grid.Column>
        ))}
      </Grid>

      <Grid stackable className='charts-grid'>
        <Grid.Column width={16}>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Header as='h3'>近 7 天模型 Token 消费堆积 (万)</Header>
              <ResponsiveContainer width='100%' height={360}>
                <BarChart data={stackedData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='date' />
                  <YAxis />
                  <Tooltip formatter={(v) => renderWan(v) + ' 万'} />
                  <Legend />
                  {models.map((m, i) => (
                    <Bar key={m} dataKey={m} stackId='a' fill={COLORS[i % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>
    </div>
  );
};

export default Dashboard;
